import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { useNotes } from './NotesContext'
import * as aiService from '../services/aiService'
import * as embeddingService from '../services/embeddingService'
import { getAllNotes } from '../services/noteService'

const AIContext = createContext()

export function AIProvider({ children }) {
  const { notes } = useNotes()
  const [isAIChatOpen, setIsAIChatOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('')
  const [modelStatus, setModelStatus] = useState({
    embeddingReady: false,
    aiReady: false,
    loading: false,
  })
  const [aiProvider, setAiProvider] = useState(aiService.getAIProvider())
  const [hasApiKey, setHasApiKey] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [backendHealth, setBackendHealth] = useState(null)
  const [embeddingProgress, setEmbeddingProgress] = useState(null)
  const [embeddedCount, setEmbeddedCount] = useState(0)
  const embeddingInitialized = useRef(false)

  // Load API key on mount
  useEffect(() => {
    const hasKey = aiService.loadOpenAIKey()
    setHasApiKey(hasKey)
    if (hasKey && aiProvider === 'openai') {
      setModelStatus(prev => ({ ...prev, aiReady: true }))
    }
  }, [aiProvider])

  // Check backend health when provider is local
  useEffect(() => {
    if (aiProvider === 'local') {
      checkHealth()
      // Check health periodically when local provider is active
      const interval = setInterval(() => {
        checkHealth()
      }, 30000) // Check every 30 seconds
      return () => clearInterval(interval)
    }
  }, [aiProvider])

  // Initialize embedding model on mount (non-blocking)
  useEffect(() => {
    if (embeddingInitialized.current) return
    embeddingInitialized.current = true

    async function loadEmbeddingModel() {
      setEmbeddingProgress({ phase: 'model', current: 0, total: 100, message: 'Loading embedding model...' })

      try {
        await embeddingService.initEmbeddingModel((progress) => {
          if (progress.status === 'progress') {
            setEmbeddingProgress({
              phase: 'model',
              current: Math.round(progress.progress || 0),
              total: 100,
              message: `Downloading model: ${Math.round(progress.progress || 0)}%`,
            })
          }
        })

        setModelStatus(prev => ({ ...prev, embeddingReady: true }))

        // Model loaded — now embed any notes that are missing embeddings
        const currentNotes = await getAllNotes()
        if (currentNotes.length > 0) {
          setEmbeddingProgress({ phase: 'indexing', current: 0, total: currentNotes.length, message: 'Indexing notes...' })
          const count = await embeddingService.embedMissingNotes(currentNotes, (current, total) => {
            setEmbeddingProgress({
              phase: 'indexing',
              current,
              total,
              message: `Indexing notes: ${current}/${total}`,
            })
            setEmbeddedCount(current)
          })
          setEmbeddedCount(currentNotes.length)
        }
      } catch (err) {
        console.error('Embedding initialization failed:', err)
        // Non-fatal: keyword search remains available
      } finally {
        setEmbeddingProgress(null)
      }
    }

    loadEmbeddingModel()
  }, [])

  // Initialize AI (for OpenAI, just check if key exists)
  async function initializeAI() {
    try {
      setModelStatus(prev => ({ ...prev, loading: true }))
      setLoadingMessage('Initializing AI...')

      // For OpenAI, we just need the API key
      if (aiProvider === 'openai' && hasApiKey) {
        setModelStatus(prev => ({ ...prev, aiReady: true }))
        setLoadingMessage('')
        console.log('AI initialized successfully!')
      } else if (aiProvider === 'openai' && !hasApiKey) {
        setModelStatus(prev => ({ ...prev, aiReady: false }))
        setLoadingMessage('OpenAI API key required. Click settings to add your key.')
      }
    } catch (error) {
      console.error('Error initializing AI:', error)
      setLoadingMessage('Error initializing AI.')
    } finally {
      setModelStatus(prev => ({ ...prev, loading: false }))
    }
  }

  // Send a message to the AI
  async function sendMessage(userMessage) {
    if (!userMessage.trim()) return

    // Add user message
    const userMsg = {
      id: Date.now(),
      role: 'user',
      content: userMessage,
      timestamp: Date.now(),
    }
    setMessages(prev => [...prev, userMsg])

    // Set loading state
    setIsLoading(true)
    setLoadingMessage('Thinking...')

    try {
      // Get AI response with RAG (using semantic or keyword search)
      const result = await aiService.answerQuestion(userMessage, (msg) => {
        setLoadingMessage(msg)
      })

      // Add AI response
      const aiMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        content: result.answer,
        relevantNotes: result.relevantNotes,
        timestamp: Date.now(),
      }
      setMessages(prev => [...prev, aiMsg])
    } catch (error) {
      console.error('Error getting AI response:', error)

      // Add error message with more detail
      const errorMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        content: hasApiKey
          ? 'Sorry, I encountered an error. Please check your API key and try again.'
          : 'Please add your OpenAI API key in settings to use the AI chat.',
        timestamp: Date.now(),
        isError: true,
      }
      setMessages(prev => [...prev, errorMsg])
    } finally {
      setIsLoading(false)
      setLoadingMessage('')
    }
  }

  // Clear chat history
  function clearChat() {
    setMessages([])
  }

  // Toggle AI chat panel
  function toggleAIChat() {
    setIsAIChatOpen(prev => !prev)

    // Initialize AI on first open if not already initialized
    if (!isAIChatOpen && !modelStatus.aiReady && !modelStatus.loading) {
      initializeAI()
    }
  }

  // Save OpenAI API key
  function saveApiKey(key) {
    aiService.setOpenAIKey(key)
    setHasApiKey(!!key)
    if (key) {
      setModelStatus(prev => ({ ...prev, aiReady: true }))
    }
  }

  // Check backend health
  async function checkHealth() {
    const healthy = await aiService.checkBackendHealth()
    setBackendHealth(healthy)
    setModelStatus(prev => ({ ...prev, aiReady: healthy }))
    return healthy
  }

  // Change AI provider
  function changeProvider(provider) {
    aiService.setAIProvider(provider)
    setAiProvider(provider)

    if (provider === 'local') {
      checkHealth()
    } else if (provider === 'openai') {
      setModelStatus(prev => ({ ...prev, aiReady: hasApiKey }))
    }
  }

  const value = {
    isAIChatOpen,
    toggleAIChat,
    messages,
    sendMessage,
    clearChat,
    isLoading,
    loadingMessage,
    modelStatus,
    embeddingStats: {
      totalNotes: notes.length,
      embeddedNotes: embeddedCount,
      missingEmbeddings: Math.max(0, notes.length - embeddedCount),
    },
    embeddingProgress,
    initializeAI,
    embedNote: async (note) => {
      if (!embeddingService.isModelReady()) return true
      try {
        await embeddingService.embedNote(note)
        return true
      } catch (err) {
        console.error('Failed to embed note:', err)
        return false
      }
    },
    saveApiKey,
    changeProvider,
    aiProvider,
    hasApiKey,
    showSettings,
    setShowSettings,
    backendHealth,
    checkHealth,
  }

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>
}

export function useAI() {
  const context = useContext(AIContext)
  if (!context) {
    throw new Error('useAI must be used within AIProvider')
  }
  return context
}

export default AIContext
